import React, { useState, useEffect } from "react";
import "./summarypage.css";
import { FaArrowLeft, FaSyncAlt, FaCalendarAlt } from "react-icons/fa";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faSliders, faCircleExclamation, faBullseye } from "@fortawesome/free-solid-svg-icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PriviewsGoalReview from "./priviewsgoalreview";
import { buildDomainTagsFromGoal } from "./goalDomainTags";
import Toast from "./Toast";
import { API_BASE } from "../apiConfig";




// -- Status options + the helper copy shown under Status / Result Date --
const STATUS_OPTIONS = [
    "In progress - on track",
    "In progress - delayed",
    "Achieved",
    "Not achieved by target date",
];

const STATUS_DESCRIPTIONS = {
    "In progress - on track":
        "Progress is occurring as expected and the goal remains realistic and feasible. Review and refine the intervention, mode, and dose with any new prognostic information and/or goal-limiting factors.",
    "In progress - delayed":
        "Progress is occurring, but it is slower than expected or has extended beyond the original timeframe. Review and refine the intervention, mode, and dose with any new prognostic information and/or goal-limiting factors.",
    "Achieved":
        "The goal has been met and new goal setting is required. Review the intervention, mode, and dose, along with prognostic information and goal-limiting factors.",
    "Not achieved by target date":
        "Little or no progress has been made toward the goal by the target date. Re-evaluation and new goal setting are required. Review the intervention, mode, and dose, along with prognostic information and goal-limiting factors.",
};

// Legacy statuses saved in the DB under the old labels are mapped to the new
// wording so existing goals render and save under the updated option set.
const STATUS_MAP = {
    "In Progress- on track": "In progress - on track",
    "In progress- on track": "In progress - on track",
    "In Progress- delayed": "In progress - delayed",
    "In progress- delayed": "In progress - delayed",
    "Not Progressed as on date": "In progress - delayed",
    "Not Progressed as on Date": "In progress - delayed",
    "Achieved": "Achieved",
    "Not Achieved as on target date": "Not achieved by target date",
    "Not achieved as on target date": "Not achieved by target date",
};

const normalizeStatus = (s) => {
    const t = String(s || "").trim();
    return STATUS_MAP[t] || t;
};

// Timestamp used to pick the newest version of a saved goal. Mirrors the
// updated_at / created_at comparison in get_goals_page.php.
const versionStamp = (v) => {
    if (!v) return 0;
    const t = Date.parse(String(v));
    return Number.isNaN(t) ? 0 : t;
};

// "Newest edit wins" between the planning goals and the latest review
// snapshot, so the Goal Review form and the Review Existing Goals table
// always agree on which SMART goal / outcome measure / dosage is current.
//
// A Goal Review appends an immutable review snapshot (goal_type = 'review');
// /updategoal ("Revise Selected Intervention") rewrites the planning goal
// row. Either one can be the most recent statement of the goal, so the
// planning row is shown when it was written/edited AFTER the last review
// snapshot - that also carries the revised SMART goal, outcome measure and
// dosage, and starts the review-only fields (outcome measure evaluation, key
// components, actions) blank instead of carrying over an old review's notes.
const newestVersionGoals = (allGoals, reviewGoals) => {
    const goalsList = Array.isArray(allGoals) ? allGoals : [];
    const reviewList = Array.isArray(reviewGoals) ? reviewGoals : [];
    const latestReview =
        reviewList.length > 0 ? reviewList[reviewList.length - 1] : null;

    const planningNewest = goalsList.reduce(
        (max, g) =>
            Math.max(max, versionStamp(g.updated_at), versionStamp(g.created_at)),
        0
    );
    const reviewNewest = latestReview
        ? latestReview.goals.reduce(
              (max, g) =>
                  Math.max(
                      max,
                      versionStamp(g.updated_at),
                      versionStamp(g.created_at)
                  ),
              versionStamp(latestReview.review_created_at)
          )
        : 0;

    if (planningNewest >= reviewNewest) return goalsList;
    return latestReview && latestReview.goals && latestReview.goals.length > 0
        ? latestReview.goals
        : goalsList;
};

// -- Small numeric stepper used for dosage / sessions counts --
const Counter = ({ label, value, onChange, min = 0, disabled = false }) => {
    const numericValue = Number(value) || 0;
    const [editing, setEditing] = useState(false);

    const handleIncrement = () => !disabled && onChange(numericValue + 1);
    const handleDecrement = () => !disabled && onChange(Math.max(min, numericValue - 1));
    const handleInputChange = (e) => {
        if (disabled) return;
        const raw = e.target.value;
        if (raw === "") {
            onChange(min);
            return;
        }
        const parsed = Number(raw);
        if (!Number.isNaN(parsed)) {
            onChange(Math.max(min, parsed));
        }
    };

    return (
        <div className="grv-counter-field">
            <label>{label}</label>
            <div className="grv-counter">
                <button type="button" onClick={handleDecrement} aria-label={`Decrease ${label}`} disabled={disabled}>
                    -
                </button>
                <input
                    type="number"
                    className="grv-counter-input"
                    value={editing ? (editing === "clear" ? "" : editing) : numericValue}
                    min={min}
                    onChange={(e) => {
                        setEditing(e.target.value);
                        handleInputChange(e);
                    }}
                    onFocus={() => {
                        if (disabled) return;
                        if (numericValue === 0) setEditing("clear");
                    }}
                    onBlur={() => {
                        setEditing(false);
                        if (numericValue === 0 && (value === 0 || value === "0" || value === "" || value == null)) {
                            onChange(0);
                        }
                    }}
                    disabled={disabled}
                    readOnly={disabled}
                />
                <button type="button" onClick={handleIncrement} aria-label={`Increase ${label}`} disabled={disabled}>
                    +
                </button>
            </div>
        </div>
    );
};

export default function GoalReviewpage() {
    const navigate = useNavigate();
    const location = useLocation();
    const token = localStorage.getItem("token");

    // -- History mode: Client History -> "Review" on a single row --
    // Opened as /goalreview-history?sessionId=..&reviewId=.. (the dedicated
    // history route), or as goalreview?sessionId=..&mode=history&reviewId=..
    // The page then shows ONE saved version with exactly the same layout as
    // the review page, minus every editing action. reviewId=0 is the
    // original Goal Set row, any other value is that review's version.
    const [searchParams] = useSearchParams();
    const isHistoryRoute = location.pathname === "/goalreview-history";
    const isHistoryView = isHistoryRoute || searchParams.get("mode") === "history";
    const historyReviewId = parseInt(searchParams.get("reviewId") || "0", 10) || 0;

    const [currentSessionId, setCurrentSessionId] = useState(null);
    const [interventionsData, setInterventionsData] = useState(null);
    const [loadingInterventions, setLoadingInterventions] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [updateButtonText, setUpdateButtonText] = useState("Update");
    const [goalClosedDate, setGoalClosedDate] = useState(null);
    const [toast, setToast] = useState(null);
    // Review session resolved for this client (used by "Continue Pathway").
    // Editing review goals implies a Review flow, so continue should resume
    // the REVIEW questions even when goalreview was opened with an initial
    // sessionId.
    const [reviewSessionId, setReviewSessionId] = useState(null);
    // Age group + client_id of the resolved review session. Passed to
    // /review-assessment on "Continue Pathway" so the Review questions open
    // with the correct age-based pathway (the review age can differ from the
    // initial goalreview session's age_group).
    const [reviewSessionAge, setReviewSessionAge] = useState(null);
    const [reviewClientId, setReviewClientId] = useState(null);
    // "review" | "review smart goal" - the mode of the session Continue Pathway
    // resumes, so it reopens on the right page/mode.
    const [reviewApptType, setReviewApptType] = useState("");
    // -- "Goal Closed" button just opens the confirmation popup --
    // A goal may only be closed once its status has been marked "Achieved".
    // Goals that are already closed (is_closed = 1) are skipped so they don't
    // block the remaining goals from being closed.
    const handleGoalClosedClick = () => {
        if (goals.length === 0) return;
        const notReady = goals.filter((goal) => {
            if (Number(goal.is_closed) === 1) return false;
            const edit = goalEdits[goal.goal_id] || {};
            const effectiveStatus = normalizeStatus(edit.status || goal.status || "");
            return effectiveStatus !== "Achieved";
        });
        if (notReady.length > 0) {
            // Read the domains the same way the Goal Domain field does, so the
            // warning names "Motor - Grasp And Release" rather than a bare
            // domain (or, on rows saved by an older build, "Motor-Grasp And
            // Release"). De-duplicated, because one goal can list several.
            const pendingLabels = [
                ...new Set(
                    notReady.flatMap((g) => buildDomainTagsFromGoal(g))
                ),
            ];
            setToast({
                message:
                    "Please mark the goal status as 'Achieved' before closing the goal. " +
                    `Goals still needing review: ${pendingLabels.join(", ")}`,
                type: "warning",
            });
            return;
        }
        setShowCloseConfirm(true);
    };

    // -- Runs when user taps "Yes, Confirm" in the popup --
    const confirmGoalClosed = async () => {
        setShowCloseConfirm(false);
        setIsSaving(true);
        try {
            goals.forEach((goal) => updateGoalEdit(goal.goal_id, "is_closed", 1));

            let allOk = true;
            for (const goal of goals) {
                const ok = await saveGoalReview(goal, { close: true });
                if (!ok) allOk = false;
            }
            if (allOk) {
                const today = new Date();
                const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sept", "oct", "nov", "dec"];
                setGoalClosedDate(`${today.getDate()} ${months[today.getMonth()]} ${today.getFullYear()}`);
                setIsEditing(false);
                setToast({ message: "All goals marked as closed.", type: "success" });
                fetchInterventions();
            } else {
                setToast({ message: "Some goals failed to close. Please try again.", type: "error" });
            }
        } finally {
            setIsSaving(false);
        }
    };
    // goalEdits: { [goal_id]: { status, resultDate, demonstratedWell, strengthened, actions, outcomeFollowUp } }
    const [goalEdits, setGoalEdits] = useState({});
    // interventionEdits: { "goalId-idx": { minutes, weeks, sessions } }
    const [interventionEdits, setInterventionEdits] = useState({});
    // Key components fetched from API per intervention excel_sheet
    const [keyComponentsMap, setKeyComponentsMap] = useState({});
    // Active Key Components tab per goal (goal_id -> family name from API).
    // Falls back to the FIRST tab when a goal has no explicit selection.
    const [activeKcTab, setActiveKcTab] = useState({});

    // -- True when goalreview was opened from the Goals page "Edit" button.
    // The "Continue Pathway" button only makes sense when arriving from the
    // Review flow (start-assessment ? Review), so it is hidden here. --
    const [fromGoalsPage, setFromGoalsPage] = useState(false);

    // -- Session ID Initialization --
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const urlSessionId = urlParams.get("sessionId");

        if (urlParams.get("fromGoalsPage") === "1") {
            setFromGoalsPage(true);
        }

        if (urlSessionId) {
            setCurrentSessionId(urlSessionId);
            return;
        }

        const savedState = localStorage.getItem("assessmentState");
        if (savedState) {
            try {
                const parsed = JSON.parse(savedState);
                if (parsed.currentSessionId) setCurrentSessionId(parsed.currentSessionId);
            } catch (e) {
                console.error("Error parsing saved state", e);
            }
        }
    }, [location.search]);

    // -- Fetch Data --
    // json.goals        -> every goal for this session, rendered editable (top section)
    // json.review_goals -> goals that ALREADY have a saved review, rendered
    //                      read-only in a separate section below (className
    //                      "grv-page getall")
    const fetchInterventions = async () => {
        if (!currentSessionId) return;

        setInterventionsData(null);
        setLoadingInterventions(true);

        try {
            const response = await fetch(
                `${API_BASE}/therapist_api/get_test_intervention.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ session_id: parseInt(currentSessionId) }),
                }
            );

            const json = await response.json();

            if (json.status && json.data) {
                setInterventionsData(json);

                const goalsList = json.goals || [];
                const reviewGoalsList = json.review_goals || [];

                // The latest review (if any) is the source of truth for the
                // editable SECTION 1 form. Editing it updates that SAME review
                // in place (via review_id) instead of creating a new review
                // form on every Update click.
                const latestReview =
                    reviewGoalsList.length > 0
                        ? reviewGoalsList[reviewGoalsList.length - 1]
                        : null;

                // History mode reads ONE specific version instead of the
                // latest: reviewId=0 shows the original planning goals
                // (Goal Set row), any other id shows that review's goals.
                const requestedReview =
                    reviewGoalsList.find(
                        (review) => Number(review.review_id) === historyReviewId
                    ) || null;

                // Editable goals = the NEWEST version (mirrors
                // get_goals_page.php): when the planning goals were written or
                // revised AFTER the last review snapshot (the "Revise Selected
                // Intervention" flow), the form is seeded from those planning
                // goals - with their current SMART goal, outcome measure and
                // dosage, and blank review-only fields. Otherwise the latest
                // review snapshot is shown, as before. History mode reads the
                // exact requested version.
                const editableGoals = isHistoryView
                    ? historyReviewId === 0
                        ? goalsList
                        : requestedReview?.goals || []
                    : newestVersionGoals(goalsList, reviewGoalsList);

                if (editableGoals.length > 0) {
                    const seededGoalEdits = {};
                    const seededInterventionEdits = {};
                    editableGoals.forEach((goal) => {
                        const firstIntervention =
                            (goal.interventions || [])[0] || {};

                        seededGoalEdits[goal.goal_id] = {
                            // A planning goal that was revised on /updategoal
                            // keeps the "Select" placeholder, so fall back to
                            // the latest review's real status - otherwise an
                            // Update click would wipe the current status out of
                            // the row (mirrors get_goals_page.php's effective
                            // status).
                            status: (() => {
                                const s = normalizeStatus(goal.status);
                                if (s && s !== "Select") return s;
                                const ls = normalizeStatus(
                                    latestReview?.goals?.[0]?.status
                                );
                                if (ls && ls !== "Select") return ls;
                                return STATUS_OPTIONS[0];
                            })(),
                            is_closed: Number(goal.is_closed) === 1 ? 1 : 0,
                            // Show the date that was actually saved for this
                            // version; only fall back to today for a goal that
                            // has never been given one.
                            resultDate: goal.result_date || getTodayDateString(),
                            dose_of_intervention:
                                firstIntervention.dose_of_intervention ??
                                goal.dose_of_intervention ??
                                "",
                            demonstratedWell:
                                goal.components_demonstrated || "",
                            strengthened:
                                goal.components_strengthened || "",
                            actions:
                                goal.components_specific_action || "",
                            outcomeFollowUp:
                                goal.outcome_measure_used || "",
                        };

                        (goal.interventions || []).forEach((iv, idx) => {
                            seededInterventionEdits[`${goal.goal_id}-${idx}`] = {
                                minutes: iv.dosage_minutes_per_day ?? 0,
                                weeks: iv.dosage_weeks ?? 0,
                                sessions: iv.therapist_sessions ?? 0,
                            };
                        });
                    });
                    setGoalEdits(seededGoalEdits);
                    setInterventionEdits(seededInterventionEdits);
                }
            }
        } catch (error) {
            console.error("Error fetching interventions:", error.message);
        } finally {
            setLoadingInterventions(false);
        }
    };

    useEffect(() => {
        if (currentSessionId) fetchInterventions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSessionId]);

    // -- Fetch Key Components for all selected interventions --
    useEffect(() => {
        const allGoals = interventionsData?.goals || [];
        const reviewGoalsList = interventionsData?.review_goals || [];
        const latestReview = reviewGoalsList.length > 0 ? reviewGoalsList[reviewGoalsList.length - 1] : null;

        // Must match the version rendered above: history mode fetches the key
        // components of the requested version, not the latest one.
        let goalsList;
        if (isHistoryView) {
            const requestedReview =
                reviewGoalsList.find(
                    (review) => Number(review.review_id) === historyReviewId
                ) || null;
            goalsList =
                historyReviewId === 0
                    ? allGoals
                    : requestedReview?.goals || [];
        } else {
            goalsList = newestVersionGoals(allGoals, reviewGoalsList);
        }

        if (goalsList.length === 0) return;

        const allSheets = new Set();
        goalsList.forEach((goal) => {
            getSelectedInterventions(goal).forEach((s) => allSheets.add(s));
        });

        if (allSheets.size === 0) return;

        const fetchKC = async () => {
            const newMap = {};
            for (const sheet of allSheets) {
                const item = findInterventionTile(sheet);
                if (!item || !item.id) continue;
                try {
                    const res = await fetch(
                        `${API_BASE}/therapist_api/get_key_components.php`,
                        {
                            method: "POST",
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                            body: JSON.stringify({
                                intervention_id: item.id,
                                session_id: parseInt(currentSessionId),
                            }),
                        }
                    );
                    const json = await res.json();
                    if (json.status && json.data?.key_components) {
                        newMap[sheet] = {
                            ...json.data.key_components,
                            family: json.data.family || sheet,
                        };
                    }
                } catch (err) {
                    console.error("Error fetching key components for", sheet, err.message);
                }
            }
            setKeyComponentsMap(newMap);
        };
        fetchKC();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interventionsData]);

    // -- Resolve the client's REVIEW session id ----------------------------
    // goalreview already knows the client via interventionsData.session
    // (client_id / client_name / age_group). Find the matching review session
    // from get_review_pathway_details.php so "Continue Pathway" resumes the
    // Review questions (appointment_type = review) instead of the initial ones.
    const resolveReviewSessionId = async () => {
        if (!token || !interventionsData || !interventionsData.session) return null;
        const s = interventionsData.session;
        const norm = (v) => String(v == null ? "" : v).trim().toLowerCase();
        try {
            const res = await fetch(
                `${API_BASE}/therapist_api/get_review_pathway_details.php`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                }
            );
            const json = await res.json();
            if (!json.status || !Array.isArray(json.data)) return null;

            const clientObj = json.data.find(
                (c) =>
                    (s.client_id != null && String(c.client_id) === String(s.client_id)) ||
                    (s.client_name && norm(c.client_name) === norm(s.client_name))
            );
            if (!clientObj) return null;

            const sessions = Array.isArray(clientObj.sessions) ? clientObj.sessions : [];
            const getAge = (ss) => norm(ss.age_group || ss.age);
            const getSid = (ss) => (ss.sessionId != null ? ss.sessionId : ss.session_id != null ? ss.session_id : null);
            // "Continue Pathway" must resume a REVIEW-type session only.
            // Resuming an initial session would re-open the initial pathway with
            // its initial answers, so an initial session must never be picked.
            //
            // "Review Smart Goal" is a peer of "Review", not a variant of it, and
            // it gets its own pathway and its own session. It used to be excluded
            // here, so a client whose only review-style session was an RSG one
            // resolved to chosen = null and "Continue Pathway" started a brand
            // new plain Review instead of resuming their work.
            const isReview = (ss) => {
                const t = (ss.appointment_type || ss.type || "").toLowerCase();
                return t === "review" || t === "review smart goal";
            };
            const reviewSessions = sessions.filter(isReview);
            // Resolve the client's review session when one exists, but still
            // return the client_id even when there is none so "Continue
            // Pathway" can create the review session from Review-assessment.
            let chosen = null;
            if (reviewSessions.length > 0) {
                // Prefer a review session matching the goalreview session's age
                // group; otherwise fall back to the most recently created one.
                const ageMatched = s.age_group
                    ? reviewSessions.filter((ss) => getAge(ss) === norm(s.age_group))
                    : reviewSessions;
                const pool = ageMatched.length > 0 ? ageMatched : reviewSessions;
                chosen = pool.reduce((best, ss) => {
                    const sid = Number(getSid(ss));
                    return best === null || sid > Number(getSid(best)) ? ss : best;
                }, null);
            }
            return {
                sessionId: chosen ? getSid(chosen) : null,
                age_group: chosen ? (chosen.age_group || chosen.age || null) : null,
                // Which of the two review modes the resolved session belongs to.
                // "Continue Pathway" has to reopen the page in that exact mode,
                // otherwise a Review Smart Goal session reopens as a plain
                // Review and the whole revision context is lost.
                appointmentType: chosen ? String(chosen.appointment_type || chosen.type || "").toLowerCase() : "",
                client_id: clientObj.client_id || null,
            };
        } catch (error) {
            console.error("Resolve review session failed:", error.message);
            return null;
        }
    };

    useEffect(() => {
        if (!interventionsData || !interventionsData.session) return;
        let cancelled = false;
        resolveReviewSessionId().then((info) => {
            if (!cancelled && info) {
                setReviewSessionId(info.sessionId);
                setReviewSessionAge(info.age_group);
                setReviewClientId(info.client_id);
                setReviewApptType(info.appointmentType || "");
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [interventionsData]);

    // input[type=date] requires YYYY-MM-DD
    const getTodayDateString = () => {
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    const formatDateDisplay = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    };

    const formatClosedDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sept", "oct", "nov", "dec"];
        return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    };

    // Some review goals store the skill merged into the domain as
    // "Domain-Skill" (e.g. "Play & Cognition-Play and Cognition" when the
    // domain has no sub-list). Normalise away the duplicate name so the chip
    // shows the domain once.
    const normalizeDomain = (s) =>
        String(s || "")
            .toLowerCase()
            .replace(/&/g, "and")
            .replace(/[^a-z0-9]/g, "");

    const cleanGoalDomain = (d) => {
        const parts = String(d || "")
            .split("-")
            .map((p) => p.trim())
            .filter(Boolean);
        if (parts.length > 1) {
            const firstNorm = normalizeDomain(parts[0]);
            if (firstNorm && parts.every((p) => normalizeDomain(p) === firstNorm)) {
                return parts[0];
            }
        }
        return d;
    };

    const updateGoalEdit = (goalId, field, value) => {
        setGoalEdits((prev) => ({
            ...prev,
            [goalId]: { ...prev[goalId], [field]: value },
        }));
    };

    const updateInterventionEdit = (key, field, value) => {
        setInterventionEdits((prev) => ({
            ...prev,
            [key]: { ...prev[key], [field]: value },
        }));
    };

    // Finds the catalog entry for an intervention by its excel_sheet value
    // (the value stored in pathway_goal_interventions.selected_intervention).
    // Used to render the key-components tiles (image + H2 description).
    const findInterventionTile = (excelSheet) => {
        if (!interventionsData?.data) return null;
        const norm = (s) => String(s || "").toLowerCase().replace(/[\s:.-]+/g, "");
        const target = norm(excelSheet);
        if (!target) return null;
        for (const key of Object.keys(interventionsData.data)) {
            const item = (interventionsData.data[key] || []).find(
                (i) => i.excel_sheet && norm(i.excel_sheet) === target
            );
            if (item) return item;
        }
        return null;
    };

    // Unique list of interventions the caregiver selected for a goal (deduped
    // across the goal's intervention rows, order preserved).
    const getSelectedInterventions = (goal) => {
        const sheets = [];
        (goal.interventions || []).forEach((iv) => {
            const raw = Array.isArray(iv.selected_intervention)
                ? iv.selected_intervention
                : typeof iv.selected_intervention === "string" && iv.selected_intervention.trim()
                    ? iv.selected_intervention.split(",")
                    : [];
            raw.forEach((s) => {
                const t = String(s || "").trim();
                if (t && !sheets.includes(t)) sheets.push(t);
            });
        });
        return sheets;
    };

    // -- Save a single goal's review to the backend --
    // Every save posts the WHOLE goal set, not just the goal that was
    // edited. Each save becomes its own new review line on the server, so
    // (a) a multi-goal session never loses the other goals' review data and
    // (b) Client History gets one new line per update instead of the same
    // line being overwritten.
    const saveGoalReview = async (goal, opts = {}) => {
        // No review_id is sent any more: the server appends a new review on
        // every save, which is what keeps the previous version visible.
        const interventionsPayloadFor = (g) =>
            (g.interventions || []).map((iv, idx) => {
                const e = interventionEdits[`${g.goal_id}-${idx}`] || {};
                return {
                    intervention_type: iv.intervention_type,
                    selected_intervention: iv.selected_intervention,
                    dosage_minutes_per_day: e.minutes,
                    dosage_weeks: e.weeks,
                    therapist_sessions: e.sessions,
                    dose_of_intervention:
                        (goalEdits[g.goal_id] || {}).dose_of_intervention,
                };
            });

        // Closing a goal must NOT overwrite the selected status (e.g.
        // "Achieved") with "Closed" - the closure is tracked separately via
        // is_closed, so the preview can still display the status the
        // therapist actually chose. The goal being saved applies its own
        // edits; the other goals carry their current values so a multi-goal
        // session always saves a complete review.
        const review_goals = goals.map((g) => {
            const isTarget = goal && g.goal_id === goal.goal_id;
            const e = goalEdits[g.goal_id] || {};

            const status = normalizeStatus(e.status) || normalizeStatus(g.status) || STATUS_OPTIONS[0];

            const closed = isTarget
                ? opts.close
                    ? 1
                    : Number(g.is_closed) === 1
                        ? 1
                        : 0
                : Number(g.is_closed) === 1
                    ? 1
                    : 0;

            return {
                goal_domain: g.goal_domain,
                goal_skill: g.goal_skill,
                status,
                status_content: STATUS_DESCRIPTIONS[status] || STATUS_DESCRIPTIONS[STATUS_OPTIONS[0]],
                smart_goal: e.smartGoal || g.smart_goal,
                target_date: e.targetDate || g.target_date,
                result_date: e.resultDate || g.result_date,
                dose_of_intervention: e.dose_of_intervention || g.dose_of_intervention,
                is_closed: closed,
                outcome_measure: g.outcome_measure,
                outcome_measure_used: e.outcomeFollowUp,
                components_demonstrated: e.demonstratedWell,
                components_strengthened: e.strengthened,
                components_specific_action: e.actions,
                interventions: interventionsPayloadFor(g),
            };
        });

        if (review_goals.length === 0) {
            return true;
        }

        const payload = {
            session_id: parseInt(currentSessionId),
            review_goals,
        };

        try {
            const response = await fetch(
                `${API_BASE}/therapist_api/insert_review_goals.php`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(payload),
                }
            );
            const json = await response.json();
            if (!json.status) {
                throw new Error(json.message || "Save failed");
            }
            return true;
        } catch (err) {
            console.error(`Failed to save goal review for goal ${goal.goal_id}:`, err.message);
            return false;
        }
    };

    // -- Single "Update" button for the whole page: saves every goal --
    // One POST for the whole page, not one per goal. The endpoint already
    // stores every goal in the payload as a single review, and posting once
    // keeps that review complete. Posting per goal used to create one review
    // per goal, which left Client History with a separate line per goal
    // instead of one line per update.
    const handleUpdateAll = async () => {
        if (goals.length === 0) return;
        setIsSaving(true);
        try {
            const ok = await saveGoalReview(goals[0]);
            if (ok) {
                setUpdateButtonText("Updated");
                setIsEditing(false);
                setToast({ message: "Goal review updated successfully.", type: "success" });
                fetchInterventions();
            } else {
                setToast({ message: "Some goals failed to save. Please try again.", type: "error" });
            }
        } finally {
            setIsSaving(false);
        }
    };

    // -- Render --
    if (loadingInterventions) {
        return (
            <div className="grv-page">
                <div className="grv-header-bar">
                    <button className="grv-back-btn" onClick={() => navigate(-1)} aria-label="Go to previous page">
                        <FaArrowLeft aria-hidden="true" /> Previous page
                    </button>
                    <h1>Goal Review</h1>
                </div>
                <p className="grv-loading" role="status" aria-live="polite">Loading goal review...</p>
                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </div>
        );
    }

    const sessionInfo = interventionsData?.session;
    const allGoals = interventionsData?.goals || [];
    const reviewGoals = interventionsData?.review_goals || [];

    // SECTION 1 shows the NEWEST version of the goal, mirroring
    // get_goals_page.php so this page and the Review Existing Goals table
    // never disagree about which smart goal / target date / dosage is
    // current. When the planning goals were revised AFTER the last review
    // snapshot (/updategoal "Revise Selected Intervention"), they win - and
    // because they are fresh rows, the review-only fields below (outcome
    // measure evaluation, key components, actions) start blank instead of
    // carrying an old review's notes. History mode is untouched: it always
    // shows the exact requested version.
    const latestReview =
        reviewGoals.length > 0 ? reviewGoals[reviewGoals.length - 1] : null;
    const currentVersionGoals =
        latestReview && latestReview.goals && latestReview.goals.length > 0
            ? latestReview.goals
            : allGoals;
    // -- History mode: which saved version is on screen --
    const historyReview =
        isHistoryView && historyReviewId > 0
            ? reviewGoals.find(
                  (review) => Number(review.review_id) === historyReviewId
              ) || null
            : null;
    // History mode renders THAT exact saved version, not the latest one:
    // reviewId=0 shows the original Goal Set (planning goals), a positive
    // reviewId shows that specific review's snapshot. Every review was
    // appended with its own rows and goal_ids, so falling back to
    // currentVersionGoals displayed the LATEST review's data on every
    // history row no matter which row was clicked.
    const goals = isHistoryView
        ? historyReviewId === 0
            ? allGoals
            : historyReview?.goals?.length
              ? historyReview.goals
              : currentVersionGoals
        : newestVersionGoals(allGoals, reviewGoals);

    // -- First-time review vs re-visiting the same form --
    // When every goal on the current form has already been closed (Goal
    // Closed), the same form has been finalized, so only "Continue
    // Pathway" is shown. Update + Goal Closed stay available while the
    // goals are still open (first review of the planning goals or a new
    // review cycle).
    const isFormClosed =
        goals.length > 0 && goals.every((g) => Number(g.is_closed) === 1);

    // -- A goal becomes read-only once its status is "Achieved" AND it has
    // been closed (Goal Closed). When every goal on the form is locked the
    // Update / Goal Closed buttons are hidden and all inputs are read-only. --
    const effectiveStatusOf = (goal) => {
        const edit = goalEdits[goal.goal_id] || {};
        return normalizeStatus(edit.status || goal.status || "");
    };

    const isLockedGoal = (goal) =>
        Number(goal.is_closed) === 1 && effectiveStatusOf(goal) === "Achieved";

    const isFormLocked = goals.length > 0 && goals.every(isLockedGoal);

    const historyLabel = isHistoryView
        ? historyReviewId === 0
            ? "Goal Set"
            : historyReview
              ? `Review ${historyReview.review_no}`
              : "Review"
        : "";

    if (!isHistoryView && (!sessionInfo || allGoals.length === 0)) {
        // -- "No, Skip." on the Intervention Planning popup --
        // From GoalsPage: go back the same way GoalsPage's "Previous page"
        // button does.  From start-assessment "Goal Progress
        // Update": behave exactly like this page's "Continue Pathway"
        // button (resume / create the client's review pathway).
        const handleNoGoalsSkip = () => {
            if (fromGoalsPage) {
                navigate("/Home");
                return;
            }
            const params = new URLSearchParams();
            if (reviewSessionId) params.set("sessionId", reviewSessionId);
            const resolveClientId = reviewClientId || sessionInfo?.client_id;
            if (resolveClientId) params.set("clientId", resolveClientId);
            let carryClient = sessionInfo?.client_name || "";
            let carryAge = reviewSessionAge || sessionInfo?.age_group || "";
            try {
                const stored = JSON.parse(localStorage.getItem("reviewCarryOver") || "null");
                if (stored && stored.session === "Review" && stored.client) {
                    const sameClient = !sessionInfo?.client_name ||
                        stored.client.trim().toLowerCase() === sessionInfo.client_name.trim().toLowerCase();
                    if (sameClient) {
                        carryClient = stored.client;
                        if (stored.ageRange) carryAge = stored.ageRange;
                    }
                }
            } catch (error) { /* ignore malformed stored value */ }
            if (carryClient) params.set("client", carryClient);
            if (carryAge) params.set("age", carryAge);
            params.set("fromGoalReview", "1");
            navigate(`/review-assessment?${params.toString()}`);
        };

        return (
            <div className="grv-page">
                <div className="grv-header-bar">
                    <button className="grv-back-btn" onClick={() => navigate(-1)} aria-label="Go to previous page">
                        <FaArrowLeft aria-hidden="true" /> Previous page
                    </button>
                    <h1>Goal Review</h1>
                </div>
                <p className="grv-loading" role="status">No goals have been saved for this session.</p>

                {/* -- Popup: Intervention Planning not done for this client -- */}
                <div className="grv-modal-overlay" role="dialog" aria-modal="true" aria-label="Intervention Planning">
                    <div className="grv-modal-card">
                        <p className="grv-modal-text">
                            Intervention Planning not done for this Client. Do you want to set goal procedure now?
                        </p>
                        <div className="grv-modal-actions">
                            <button
                                className="grv-modal-yes"
                                disabled={!currentSessionId}
                                onClick={() => navigate(`/updategoal?sessionId=${currentSessionId}`)}
                            >
                                Yes, Set Goal Procedure
                            </button>
                            <button className="grv-modal-no" onClick={handleNoGoalsSkip}>
                                No, Skip.
                            </button>
                        </div>
                    </div>
                </div>

                {/* Modal styles duplicated here because the shared grv-modal-*
                    rules live inside the main return's style block, which is
                    not mounted in this early-return state. */}
                <style>{`
                    .grv-modal-overlay {
                        position: fixed;
                        inset: 0;
                        background: rgba(0, 0, 0, 0.45);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        z-index: 1000;
                        padding: 16px;
                    }
                    .grv-modal-card {
                        background: #fff;
                        border-radius: 14px;
                        width: 100%;
                        max-width: 360px;
                        padding: 28px 24px 22px;
                        text-align: center;
                        position: relative;
                        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
                    }
                    .grv-modal-text {
                        font-size: 14px;
                        color: #555;
                        line-height: 1.5;
                        margin: 0 0 22px;
                    }
                    .grv-modal-actions {
                        display: flex;
                        gap: 12px;
                        justify-content: center;
                    }
                    .grv-modal-yes,
                    .grv-modal-no {
                        border: none;
                        border-radius: 8px;
                        padding: 10px 18px;
                        font-size: 14px;
                        font-weight: 600;
                        cursor: pointer;
                        flex: 1;
                    }
                    .grv-modal-yes {
                        background: #247A33;
                        color: #fff;
                    }
                    .grv-modal-yes:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }
                    .grv-modal-no {
                        background: #d32f2f;
                        color: #fff;
                    }
                `}</style>
                {toast && (
                    <Toast
                        message={toast.message}
                        type={toast.type}
                        onClose={() => setToast(null)}
                    />
                )}
            </div>
        );
    }

    return (
        <>
            {/* ------------------------------------------------------
                SECTION 1 ? GOAL REVIEW SUMMARY
                Displays the summary of the session's assessment findings
                and goals pulled from get_test_intervention.
                ------------------------------------------------------ */}
            <div className="grv-page">
                <div className="grv-header-bar">
                    <button className="grv-back-btn2" onClick={() => navigate(-1)} aria-label="Go to previous page">
                        <FaArrowLeft aria-hidden="true" /> Previous page
                    </button>
                    <h1 >Goal Review</h1>
                </div>

                <div className="grv-content">
                    <div className="summ-container11">
                        <div className="sum-cont11">

                            {/* Section 1 - Row 1: Client name (centered, cpa color) */}
                            <div className="summ-client-row">
                                {interventionsData?.session?.client_name || "Client"}
                            </div>

                            {/* Section 1 - Row 2: Metadata pills */}
                            <div className="summ-meta-row" >
                                <span className="summ-meta-pill summ-meta-plain">
                                    <span className="summ-meta-label" style={{ color: "#247A33", fontSize: "16px" }}>Assessment Date:</span>&nbsp;
                                    <span className="summ-meta-value" style={{ color: "#247A33" }}>
                                        {(() => {
                                            const code = String(interventionsData?.session?.client_code || "");
                                            const digits = code.replace(/\D/g, "").slice(0, 8);
                                            if (digits.length === 8) {
                                                const dd = digits.slice(0, 2);
                                                const mm = digits.slice(2, 4);
                                                const yyyy = digits.slice(4, 8);
                                                const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
                                                const mIdx = parseInt(mm, 10) - 1;
                                                return `${parseInt(dd, 10)} ${months[mIdx]} ${yyyy}`;
                                            }
                                            return "";
                                        })()}
                                    </span>
                                </span>
                                <span className="summ-meta-pill summ-meta-green">
                                    <span className="summ-meta-label" style={{ color: "#626A6F" }}>Client ID:</span>&nbsp;
                                    <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.client_code || ""}</span>
                                </span>
                                <span className="summ-meta-pill summ-meta-green">
                                    <span className="summ-meta-label" style={{ color: "#626A6F" }}>Age Group:</span>&nbsp;
                                    <span className="summ-meta-value" style={{ color: "#247A33" }}>{interventionsData?.session?.age_group || ""}</span>
                                </span>
                                {isHistoryView && (
                                    <span className="summ-meta-pill summ-meta-green">
                                        <span className="summ-meta-label" style={{ color: "#626A6F" }}>Version:</span>&nbsp;
                                        <span className="summ-meta-value" style={{ color: "#247A33" }}>{historyLabel} (read only)</span>
                                    </span>
                                )}
                            </div>

                            {/* Section 1 - Row 3: Section title */}
                            <h3 className="summ-section-title">Interpreting Medical History and Combined Assessment Findings</h3>

                            {/* Section 1 - Row 4: Topography + Severity */}
                            <div className="summ-findings-block">
                                <div className="summ-finding-row">
                                    <span className="icon-circle1"><FontAwesomeIcon icon={faLocationDot} aria-hidden="true" /></span>
                                    <strong className="summ-finding-label">Topography:&nbsp;</strong>
                                    <span className="summ-value-pill">{interventionsData?.session?.topography || ""}</span>
                                </div>
                                <div className="summ-finding-row">
                                    <span className="icon-circle1"><FontAwesomeIcon icon={faSliders} aria-hidden="true" /></span>
                                    <strong className="summ-finding-label">Severity:&nbsp;</strong>
                                    <span className="summ-value-pill">{interventionsData?.session?.severity || ""}</span>
                                </div>
                            </div>

                            {/* Section 1 - Row 5: Early indicators of comorbidities */}
                            <div className="summ-finding-row">
                                <span className="icon-circle1"><FontAwesomeIcon icon={faCircleExclamation} aria-hidden="true" /></span>
                                <strong className="summ-finding-label">Early indicators of comorbidities:&nbsp;</strong>
                                <span className="summ-value-pill">
                                    {interventionsData?.session?.comorbidities?.length
                                        ? interventionsData.session.comorbidities.map(item => item.type).join(", ")
                                        : ""}
                                </span>
                            </div>
                            <div className="summ-finding-row">
                                <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                                <strong className="summ-finding-label">Goal Limiting Factors:&nbsp;</strong>
                                <span className="summ-value-pill">
                                    {(() => {
                                        const factors = interventionsData?.session?.goal_limiting_factors || [];
                                        return factors.join(", ");
                                    })()}
                                </span>
                            </div>

                            {/* Section 1 - Row 6: Goals */}
                            <div className="summ-finding-row">
                                <span className="icon-circle1"><FontAwesomeIcon icon={faBullseye} aria-hidden="true" /></span>
                                <strong className="summ-finding-label">Goals:&nbsp;</strong>
                                <span className="summ-value-pill">
                                    {(() => {
                                        const map = interventionsData?.data || {};
                                        const tabs = Object.keys(map).map((key) => {
                                            const items = map[key] || [];
                                            const first = items[0] || {};
                                            const domain = first.goal_domain || key || "";
                                            const skill = first.goal_skill || "";
                                            return skill && skill !== "null" ? `${domain} - ${skill}` : domain;
                                        });
                                        return [...new Set(tabs)].join(", ");
                                    })()}
                                </span>
                            </div>
                        </div>
                    </div>

                    {goals.map((goal, gIndex) => {
                        const edit = goalEdits[goal.goal_id] || {};
                        /* Not every payload shape is an array (saved review
                           goals can omit the key or send an object), and
                           `.map` on a non-array would throw and blank the
                           whole card. Normalise it so the no-data placeholder
                           below is always reachable. */
                        const goalInterventions = Array.isArray(goal.interventions)
                            ? goal.interventions.filter(Boolean)
                            : [];
                        /* A row can exist yet carry no actual caregiver
                           selection. `hasAnySelection` is the single gate for
                           the whole section: when it is false we render ONLY
                           the "no data" placeholder and skip every input that
                           depends on a selection (dosage counters, sessions,
                           outcome, key components, reflection prompts). */
                        const hasAnySelection = goalInterventions.some((iv) => {
                            const sel = iv?.selected_intervention;
                            if (Array.isArray(sel)) return sel.some((s) => String(s).trim());
                            return typeof sel === "string" && sel.trim() !== "";
                        });
                        const isLocked = isLockedGoal(goal);
                        const isLastGoal = gIndex === goals.length - 1;

                        return (
                            <div
                                className={`grv-goal-block${isLocked ? " grv-goal-locked" : ""}${isLastGoal ? " grv-goal-block-last" : ""}`}
                                key={goal.goal_id}
                            >
                                <div className="grv-row-2col">
                                    <div className="grv-field">
                                        <label>Goal Domain </label>
                                        <div className="grv-box" style={{ padding: "7px 10px" }}>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
                                                {buildDomainTagsFromGoal(goal).map((tag, i) => (
                                                    <span key={i} style={{ background: "#e0e0e0", color: "#323C43", borderRadius: "4px", padding: "2px 10px", fontSize: "13px", fontWeight: "600", display: "inline-block" }}>{tag}</span>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="grv-field">
                                        <label>Goal Target Date</label>
                                        {isEditing && !isLocked ? (
                                            <div className="grv-date-wrapper">
                                                <input
                                                    type="date"
                                                    className="grv-box grv-date-input"
                                                    value={edit.targetDate || (goal.target_date || "").slice(0, 10)}
                                                    onChange={(e) =>
                                                        updateGoalEdit(goal.goal_id, "targetDate", e.target.value)
                                                    }
                                                    aria-label="Goal target date"
                                                />
                                                <FaCalendarAlt className="grv-date-icon" aria-hidden="true"
                                                    onClick={() => document.querySelector(".grv-date-wrapper input[type='date']")?.showPicker()} />
                                            </div>
                                        ) : (
                                            <div className="grv-box">{formatDateDisplay(edit.targetDate || goal.target_date)}</div>
                                        )}
                                    </div>
                                </div>

                                <div className="grv-field full">
                                    <label>Caregiver-led SMART goal</label>
                                    {isEditing && !isLocked ? (
                                        <input
                                            type="text"
                                            className="grv-select"
                                            value={edit.smartGoal || goal.smart_goal || ""}
                                            onChange={(e) =>
                                                updateGoalEdit(goal.goal_id, "smartGoal", e.target.value)
                                            }
                                            aria-label="Caregiver-led SMART goal"
                                        />
                                    ) : (
                                        <div className="grv-box multiline">{goal.smart_goal || "-"}</div>
                                    )}
                                </div>

                                <div className="grv-row-2col grv-status-date-row">
                                    <div className="grv-field">
                                        <label>Goal Status</label>
                                        <p className="grv-subheading">
                                            Please update the current status of the caregiver-led SMART goal.
                                        </p>
                                        <select
                                            className="grv-select"
                                            value={edit.status || STATUS_OPTIONS[0]}
                                            onChange={(e) =>
                                                updateGoalEdit(goal.goal_id, "status", e.target.value)
                                            }
                                            disabled={!isEditing || isLocked}
                                            aria-label={`Goal status for ${cleanGoalDomain(goal.goal_domain)}`}
                                        >
                                            {STATUS_OPTIONS.map((opt) => (
                                                <option key={opt} value={opt}>
                                                    {opt}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="grv-field" style={{ display: "none" }}>
                                        <label>Result Date</label>
                                        <input
                                            type="date"
                                            className="grv-box grv-date-input"
                                            value={edit.resultDate || ""}
                                            onChange={(e) =>
                                                updateGoalEdit(goal.goal_id, "resultDate", e.target.value)
                                            }
                                            readOnly={!isEditing || isLocked}
                                        />
                                    </div>
                                </div>

                                <div className="grv-status-note">
                                    {STATUS_DESCRIPTIONS[normalizeStatus(edit.status)] || STATUS_DESCRIPTIONS[STATUS_OPTIONS[0]]}
                                </div>

                                <label className="grv-label">
                                    What intervention/s has been selected by caregivers?
                                </label>


                                {!hasAnySelection ? (
                                    /* .grv-box carries no CSS rule anywhere, so
                                       style the placeholder inline to guarantee it
                                       is visible instead of collapsing. */
                                    <div
                                        style={{
                                            marginTop: "10px",
                                            padding: "10px 14px",
                                            minHeight: "40px",
                                            display: "flex",
                                            alignItems: "center",
                                            border: "1px solid #d5dbe0",
                                            borderRadius: 8,
                                            background: "#f9f9f9",
                                            color: "#999",
                                            fontSize: 14,
                                        }}
                                    >
                                        No interventions selected for this goal.
                                    </div>
                                ) : (
                                    goalInterventions.map((iv, idx) => {
                                        const editKey = `${goal.goal_id}-${idx}`;
                                        const ivEdit = interventionEdits[editKey] || {};

                                        return (
                                            <div className="grv-intervention-block" key={editKey}>
                                                <div className="grv-selection-list">
                                                    {(() => {
                                                        const selections = Array.isArray(iv.selected_intervention)
                                                            ? iv.selected_intervention
                                                            : typeof iv.selected_intervention === "string" && iv.selected_intervention.trim()
                                                                ? iv.selected_intervention.split(",").map(s => s.trim()).filter(Boolean)
                                                                : [];
                                                        if (selections.length === 0) {
                                                            return (
                                                                <div style={{ padding: "10px 14px", fontSize: 14, color: "#999" }}>
                                                                    N/A
                                                                </div>
                                                            );
                                                        }
                                                        return selections.map((item, i2) => (
                                                            <label
                                                                className="grv-selection-option"
                                                                key={i2}
                                                                style={{
                                                                    color: "#323C43",
                                                                    fontWeight: 700,
                                                                    background: "#EBF7E9",
                                                                    cursor: isLocked ? "default" : "pointer",
                                                                }}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked
                                                                    onChange={() => {}}
                                                                    disabled={!isEditing || isLocked}
                                                                    aria-label={item}
                                                                />
                                                                <span style={{ color: "#323C43", fontWeight: 700 }}>{item}</span>
                                                            </label>
                                                        ));
                                                    })()}
                                                </div>


                                                <div className="grv-sub-title">Dosage of Home Practice to Support this Goal</div>
                                                <p className="grv-sub-note">Review the dosage of intervention that was recommended to achieve this goal. Modify the fields below to best reflect the dosage that was achieved.</p>
                                                <div className="grv-counters-3col">
                                                    <Counter
                                                        label="Number of minutes per day"
                                                        value={ivEdit.minutes ?? 0}
                                                        onChange={(v) => updateInterventionEdit(editKey, "minutes", v)}
                                                        disabled={!isEditing || isLocked}
                                                    />
                                                    <Counter
                                                        label="Number of weeks"
                                                        value={ivEdit.weeks ?? 0}
                                                        onChange={(v) => updateInterventionEdit(editKey, "weeks", v)}
                                                        disabled={!isEditing || isLocked}
                                                    />
                                                </div>

                                                <div className="grv-sub-title">Therapists Sessions to Support this Goal</div>
                                                <div className="grv-counters-1col">
                                                    <Counter
                                                        label="Number of Sessions"
                                                        value={ivEdit.sessions ?? 0}
                                                        onChange={(v) => updateInterventionEdit(editKey, "sessions", v)}
                                                        disabled={!isEditing || isLocked}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })
                                )}

                                {hasAnySelection && (
                                    <>
                                        <div className="grv-outcome-2col">
                                            <div className="grv-outcome-col">
                                                <label className="grv-label">Outcome measure used</label>
                                                <div className="grv-box multiline">{goal.outcome_measure || "-"}</div>
                                            </div>
                                            <div className="grv-outcome-col">
                                                <label className="grv-label">Outcome Measure Evaluation</label>
                                                <textarea
                                                    className="grv-box grv-outcome-eval"
                                                    placeholder="Insert any follow up outcome ratings below:"
                                                    value={edit.outcomeFollowUp || ""}
                                                    onChange={(e) =>
                                                        updateGoalEdit(
                                                            goal.goal_id,
                                                            "outcomeFollowUp",
                                                            e.target.value
                                                        )
                                                    }
                                                    readOnly={!isEditing || isLocked}
                                                />
                                            </div>
                                        </div>
                                        <h3 className="grv-section-title">
                                            Review Key Components of Intervention Offered, Mode and Dose:
                                        </h3>
                                        {(() => {
                                            const sheets = getSelectedInterventions(goal);
                                            // One tab per "family" returned by get_key_components.php
                                            // (deduped, order follows the selected interventions).
                                            const kcEntries = [];
                                            const seenFamilies = new Set();
                                            sheets.forEach((sheet) => {
                                                const data = keyComponentsMap[sheet];
                                                if (!data) return;
                                                if (seenFamilies.has(data.family)) return;
                                                seenFamilies.add(data.family);
                                                kcEntries.push(data);
                                            });

                                            if (kcEntries.length === 0) return null;

                                            // Default to the FIRST tab until the user picks one
                                            const selectedFamily =
                                                activeKcTab[goal.goal_id] &&
                                                    kcEntries.some(
                                                        (e) => e.family === activeKcTab[goal.goal_id]
                                                    )
                                                    ? activeKcTab[goal.goal_id]
                                                    : kcEntries[0].family;
                                            const active = kcEntries.find(
                                                (e) => e.family === selectedFamily
                                            );

                                            return (
                                                <>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            flexWrap: "wrap",
                                                            gap: "8px",
                                                            margin: "10px 0 12px",
                                                        }}
                                                    >
                                                        {kcEntries.map((entry) => (
                                                            <button
                                                                key={entry.family}
                                                                type="button"
                                                                className={`toggle-btn3 grv-kc-tab ${selectedFamily === entry.family ? "active" : ""
                                                                    }`}
                                                                onClick={() =>
                                                                    setActiveKcTab((prev) => ({
                                                                        ...prev,
                                                                        [goal.goal_id]: entry.family,
                                                                    }))
                                                                }
                                                            >
                                                                {entry.family}
                                                            </button>
                                                        ))}
                                                    </div>
                                                    <div className="grv-kc-panel">
                                                        {active.title && (
                                                            <h4 className="grv-principles-title">
                                                                {active.title}
                                                            </h4>
                                                        )}
                                                        {active.description && (
                                                            <div
                                                                className="grv-principle-text"
                                                                dangerouslySetInnerHTML={{
                                                                    __html: active.description,
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </>
                                            );
                                        })()}

                                        <p className="grv-reflect-instructions">
                                            Reflect on the intervention as a whole and all of the key components.
                                            You do not need to rate each component separately. Use the prompts
                                            below to identify strengths, areas for improvement, and next steps.
                                        </p>

                                        <div className="grv-field-inline">
                                            <label>What key components were demonstrated well?</label>
                                            <textarea
                                                placeholder="Type Here."
                                                value={edit.demonstratedWell || ""}
                                                onChange={(e) =>
                                                    updateGoalEdit(
                                                        goal.goal_id,
                                                        "demonstratedWell",
                                                        e.target.value
                                                    )
                                                }
                                                readOnly={!isEditing || isLocked}
                                            />
                                        </div>
                                        <div className="grv-field-inline">
                                            <label>What key components could be strengthened further?</label>
                                            <textarea
                                                placeholder="Type Here..."
                                                value={edit.strengthened || ""}
                                                onChange={(e) =>
                                                    updateGoalEdit(goal.goal_id, "strengthened", e.target.value)
                                                }
                                                readOnly={!isEditing || isLocked}
                                            />
                                        </div>
                                        <div className="grv-field-inline">
                                            <label>
                                                Note any specific actions you can change when delivering this
                                                intervention.
                                            </label>
                                            <textarea
                                                placeholder="Type Here..."
                                                value={edit.actions || ""}
                                                onChange={(e) =>
                                                    updateGoalEdit(goal.goal_id, "actions", e.target.value)
                                                }
                                                readOnly={!isEditing || isLocked}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        );
                    })}

                    <div className="grv-footer">
                        {isFormLocked || goalClosedDate ? (
                            <button className="grv-close-btn" disabled style={{ opacity: 0.7, cursor: "not-allowed" }}>
                                Goal closed on {goalClosedDate || (() => {
                                    const closedGoal = goals.find(g => Number(g.is_closed) === 1);
                                    if (closedGoal && closedGoal.result_date) return formatClosedDate(closedGoal.result_date);
                                    return formatDateDisplay(getTodayDateString());
                                })()}
                            </button>
                        ) : (
                            <>
                                {/* History mode is a read-only snapshot, so every
                                    action that changes or advances the goals is
                                    removed. Only Exit stays. */}
                                {!isHistoryView && (
                                <>
                                <button
                                    className="grv-review-btn"
                                    onClick={async () => {
                                        // Entry point #3: the Review Smart Goal
                                        // pathway. Carries the client identity,
                                        // the age group and the originating
                                        // session (the one holding the goal being
                                        // reviewed) so start-assessment can
                                        // prefill and later revise the right row.
                                        const params = new URLSearchParams();
                                        if (sessionInfo?.client_name) params.set("client", sessionInfo.client_name);
                                        params.set("appointment", "Review Smart Goal");
                                        params.set("fromGoalReview", "1");
                                        const resolveClientId = reviewClientId || sessionInfo?.client_id;
                                        if (resolveClientId) params.set("clientId", resolveClientId);
                                        const resolveAge = reviewSessionAge || sessionInfo?.age_group;
                                        if (resolveAge) params.set("age", resolveAge);
                                        /*
                                         * goalSessionId names the session that
                                         * owns the goals shown on this page
                                         * (currentSessionId). The therapist got
                                         * here from a specific row of "Review
                                         * Existing Goals", so those goals are the
                                         * ones being reviewed - whether the row
                                         * sits on an Initial session or on a
                                         * Review session that itself saved the
                                         * goal (a goal-review row). Passing it
                                         * lets /start-assessment prefill from
                                         * exactly that session and later revise
                                         * that same goal, instead of inheriting
                                         * from an unrelated session's goals.
                                         */
                                        if (currentSessionId) {
                                            params.set("goalSessionId", String(currentSessionId));
                                        }

                                        /*
                                         * Resume the client's existing Review
                                         * Smart Goal session when there is one.
                                         *
                                         * Without ?sessionId= start-assessment
                                         * restores nothing and begins with an
                                         * empty answer set, so the therapist has
                                         * to re-answer every question they had
                                         * already completed on a previous pass.
                                         *
                                         * Only a Review SMART GOAL session
                                         * qualifies. A plain Review session must
                                         * not be resumed here: both modes run the
                                         * same `review` pathway, so the draft
                                         * would reopen as a plain Review and
                                         * contradict the appointment above.
                                         */
                                        try {
                                            const res = await fetch(
                                                `${API_BASE}/therapist_api/get_smart_goal_context.php`,
                                                {
                                                    method: "POST",
                                                    headers: {
                                                        "Content-Type": "application/json",
                                                        Authorization: `Bearer ${token}`,
                                                    },
                                                    body: JSON.stringify({
                                                        client_id: resolveClientId ? parseInt(resolveClientId) : 0,
                                                        goal_session_id: currentSessionId ? parseInt(currentSessionId) : 0,
                                                    }),
                                                }
                                            );
                                            const json = await res.json();
                                            const resumeId = Number(json?.reviewSessionId || 0);
                                            if (json?.status && resumeId > 0) {
                                                params.set("sessionId", String(resumeId));
                                            }
                                        } catch (err) {
                                            // Not fatal. With no resume target the
                                            // flow still runs; it just starts a
                                            // fresh Review Smart Goal session.
                                            console.error("Could not resolve Review Smart Goal session to resume:", err.message);
                                        }

                                        navigate(`/start-assessment?${params.toString()}`);
                                    }}
                                    disabled={isSaving}
                                >
                                    Review Assessment
                                </button>
                                {!isEditing ? (
                                    <button
                                        className="grv-update-btn"
                                        onClick={() => { setIsEditing(true); setUpdateButtonText("Update"); }}
                                        disabled={isSaving}
                                    >
                                        Edit
                                    </button>
                                ) : (
                                    <button
                                        className="grv-update-btn"
                                        onClick={handleUpdateAll}
                                        disabled={isSaving}
                                    >
                                        {isSaving ? "Saving.." : updateButtonText}
                                        <FaSyncAlt className={isSaving ? "grv-spin" : ""} aria-hidden="true" />
                                    </button>
                                )}
                                {!isEditing && updateButtonText === "Updated" && (
                                    <button className="grv-update-btn" disabled style={{ opacity: 0.7, cursor: "not-allowed" }}>
                                        Updated
                                    </button>
                                )}
                                <button
                                    className="grv-close-btn"
                                    onClick={handleGoalClosedClick}
                                    disabled={isSaving || isFormClosed}
                                >
                                    Goal Closed
                                </button>
                                {!fromGoalsPage && (
                                    <button
                                        className="grv-close-btn"
                                        onClick={() => {
                                            const params = new URLSearchParams();
                                            if (reviewSessionId) params.set("sessionId", reviewSessionId);
                                            const resolveClientId = reviewClientId || sessionInfo?.client_id;
                                            if (resolveClientId) params.set("clientId", resolveClientId);
                                            let carryClient = sessionInfo?.client_name || "";
                                            let carryAge = reviewSessionAge || sessionInfo?.age_group || "";
                                            try {
                                                const stored = JSON.parse(localStorage.getItem("reviewCarryOver") || "null");
                                                if (stored && stored.session === "Review" && stored.client) {
                                                    const sameClient = !sessionInfo?.client_name ||
                                                        stored.client.trim().toLowerCase() === sessionInfo.client_name.trim().toLowerCase();
                                                    if (sameClient) {
                                                        carryClient = stored.client;
                                                        if (stored.ageRange) carryAge = stored.ageRange;
                                                    }
                                                }
                                            } catch (error) { /* ignore malformed stored value */ }
                                            if (carryClient) params.set("client", carryClient);
                                            if (carryAge) params.set("age", carryAge);
                                            params.set("fromGoalReview", "1");

                                            // All appointments are driven from
                                            // /start-assessment, so that is where
                                            // this returns. It used to point at the
                                            // standalone /review-assessment page,
                                            // which cannot tell a Review Smart
                                            // Goal from a plain Review - an RSG
                                            // session therefore reopened in the
                                            // wrong mode and lost its goal context.
                                            //
                                            // The `appointment` param is required:
                                            // without it the page opens the Initial
                                            // step-1 form.
                                            const isSmartGoalSession = reviewApptType === "review smart goal";
                                            params.set("appointment", isSmartGoalSession ? "Review Smart Goal" : "Review");
                                            // Carry the goal being revised, so
                                            // /start-assessment re-fetches it and
                                            // the RSG step-1 card can show it.
                                            if (isSmartGoalSession) {
                                                let carriedGoalSessionId = "";
                                                try {
                                                    const raw = JSON.parse(localStorage.getItem("reviewCarryOver") || "null");
                                                    carriedGoalSessionId = raw?.goalSessionId ? String(raw.goalSessionId) : "";
                                                } catch (error) { /* ignore malformed stored value */ }
                                                if (carriedGoalSessionId) params.set("goalSessionId", carriedGoalSessionId);
                                            }
                                            navigate(`/start-assessment?${params.toString()}`);
                                        }}
                                        disabled={isSaving}
                                    >
                                        Continue Pathway
                                    </button>
                                )}
                                </>
                                )}
                                <button
                                    className="grv-close-btn"
                                    onClick={() => navigate("/Home")}
                                    disabled={isSaving}
                                >
                                    Exit
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <PriviewsGoalReview sessionId={currentSessionId} />

            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {showCloseConfirm && (
                <div className="grv-modal-overlay" onClick={() => setShowCloseConfirm(false)} onKeyDown={(e) => { if (e.key === "Escape") { e.preventDefault(); setShowCloseConfirm(false); } }} role="dialog" aria-modal="true" aria-label="Confirm close goal">
                    <div className="grv-modal-card" onClick={(e) => e.stopPropagation()}>
                        <button
                            className="grv-modal-close-x"
                            onClick={() => setShowCloseConfirm(false)}
                            aria-label="Close"
                        >
                            &#10005;
                        </button>
                        <div className="grv-modal-icon">&#9888;</div>
                        <h3 className="grv-modal-title">Confirmation</h3>
                        <p className="grv-modal-text">
                            Is the goal achieved or closed? You will not be able to edit the
                            details after this goal is closed.
                        </p>
                        <div className="grv-modal-actions">
                            <button className="grv-modal-yes" onClick={confirmGoalClosed}>
                                Yes, Confirm
                            </button>
                            <button
                                className="grv-modal-no"
                                onClick={() => setShowCloseConfirm(false)}
                            >
                                No, cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* -- Styles matching the design mock-up, shared by both sections -- */}
            <style>{`
                .grv-page {
                    width: 100%;
                    max-width: 1600px;
                    margin: 0 auto;
                    background: #ffffff;
                    min-height: 100vh;
                    padding-bottom: 40px;
                }
                .grv-page.getall {
                    margin-top: 24px;
                }
                .grv-readonly {
                    background: #eceeef !important;
                    color: #666 !important;
                    cursor: not-allowed;
                    user-select: text;
                }
                .grv-goal-locked input,
                .grv-goal-locked select,
                .grv-goal-locked textarea {
                    background: #e0e0e0 !important;
                    color: #323C43 !important;
                }
                .grv-header-bar {
                    position: relative;
                    background: #ffffff;
                    color: #247A33;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 14px 16px;
                    min-height: 50px;
                }
                .grv-header-bar h2 {
                    margin: 0;
                    font-size: 20px;
                    font-weight: 700;
                }
                .grv-back-btn {
                    position: absolute;
                    left: 12px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: white;
                    border: 1px solid rgba(255,255,255,0.6);
                    color: #247A33;
                    border-radius: 5px;
                    padding: 6px 12px;
                    font-size: 13px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    cursor: pointer;
                    font-weight: 700;
                }
                .grv-back-btn2 {
                    position: absolute;
                    left: 20px;
                    top: 50%;
                    transform: translateY(-50%);
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    background-color: #fff;
                    border: 1.5px solid #247A33;
                    color: #247A33;
                    font-size: 15px;
                    font-weight: 500;
                    padding: 8px 16px;
                    border-radius: 6px;
                    cursor: pointer;
                    text-decoration: none;
                    transition: all 0.2s ease-in-out;
                }
                .grv-back-btn2 .back-icon,
                .grv-back-btn2 svg {
                    font-size: 16px;
                }
                .grv-back-btn2:hover {
                    color: #247A33;
                    border-color: #047df6;
                    text-decoration: underline;
                }
                .grv-content {
                    padding: 18px 70px 10px;
                }
                .grv-loading {
                    padding: 24px 20px;
                    text-align: center;
                }
                .grv-client-name {
                    text-align: center;
                    color: #247A33;
                    font-size: 22px;
                    margin: 4px 0 2px;
                }
                .grv-assessment-date {
                    text-align: left;
                    color: #247A33;
                    font-weight: 600;
                    margin: 0 0 18px;
                    font-size:24px;
                }
                .grv-goal-block {
                    border-bottom: 2px solid #b6d7b6;
                    padding-bottom: 26px;
                    margin-bottom: 26px;
                }
                .grv-goal-block:last-child {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .grv-goal-block.grv-goal-block-last {
                    border-bottom: none;
                    margin-bottom: 0;
                    padding-bottom: 0;
                }
                .grv-row-2col {
                    display: grid;
                    grid-template-columns: 1fr 1fr 1fr;
                    gap: 16px;
                    margin-bottom: 20px;
                }
                .grv-field {
                    display: flex;
                    flex-direction: column;
                }
                .grv-field.full {
                    margin-bottom: 20px;
                }
                .grv-field label,
                .grv-label {
                    font-size: 16px;
                    color: #247A33;
                    font-weight: 700;
                    margin-bottom: 6px;
                    display: block;
                }
                .grv-box {
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 9px 12px;
                    font-size: 14px;
                    background: #f0f0f0;
                    color: #555;
                }
                .grv-box.multiline {
                    min-height: 44px;
                }
                .grv-date-input {
                    width: 100%;
                    box-sizing: border-box;
                    font-family: inherit;
                    background: #fff;
                    color: #333;
                }
                .grv-date-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                .grv-date-icon {
                    position: absolute;
                    right: 12px;
                    color: #247A33;
                    font-size: 16px;
                    cursor: pointer;
                    pointer-events: auto;
                    background: #fff;
                }
                .grv-select {
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 9px 12px;
                    font-size: 14px;
                    background: #fff;
                    color: #333;
                    width: 100%;
                    box-sizing: border-box;
                }
                .grv-subheading {
                    font-weight: 500;
                    font-style: italic;
                    color: #263238;
                    margin: 4px 0 12px;
                    font-size: 13px;
                }
                .grv-status-note {
                    background: #edeaea;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 12px 14px;
                    font-size: 16px;
                    color: #333;
                    line-height: 1.6;
                    margin-bottom: 20px;
                }
                .intervention-tags1 {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 8px;
                    margin: 10px 0 18px;
                }
                .intervention-tag1 {
                    background: #f0f0f0;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 16px;
                    padding: 6px 14px;
                    font-size: 13px;
                }
                .grv-intervention-block {
                    padding-top: 0px;
                    margin-top: 18px;
                    margin-buttom:20px;
                }
                .grv-intervention-row {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 20px;
                    font-weight: 700;
                    color: #247A33;
                }
                .grv-pill {
                    background: #f0f0f0;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 5px;
                    padding: 6px 14px;
                    font-size: 13px;
                }
                .grv-selection-list {
                    border: 1px solid #d5dbe0;
                    border-radius: 8px;
                    overflow: hidden;
                    margin-bottom: 20px;
                }
                .grv-selection-option {
                    display: flex;
                    align-items: flex-start;
                    gap: 10px;
                    padding: 10px 14px;
                    font-size: 14px;
                    line-height: 1.5;
                    cursor: pointer;
                    white-space: normal;
                    word-break: break-word;
                    border-bottom: 1px solid #eef0f2;
                }
                .grv-selection-option:last-child {
                    border-bottom: none;
                }
                .grv-selection-option input[type="checkbox"] {
                    margin: 3px 0 0;
                    flex-shrink: 0;
                    width: 16px;
                    height: 16px;
                }
                .grv-selection-option span {
                    flex: 1;
                    text-align: left;
                }
                .grv-sub-title {
                    font-weight: 700;
                    color: #247A33;
                    margin: 14px 0 10px;
                    font-size: 14px;
                    text-align: left;
                }
                .grv-sub-note {
                    font-size: 14px;
                    line-height: 1.5;
                    color: #030303;
                    margin: -4px 0 12px;
                }
                .grv-counters-3col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                    margin-bottom: 4px;
                }
                .grv-counters-1col {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 4px;
                }
                .grv-counter-field {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    justify-content: space-between;
                    gap: 16px;
                    max-width: 320px;
                    margin-bottom: 20px;
                }
                .grv-counter-field label {
                    font-size: 13px;
                    color: #247A33;
                    font-weight: 700;
                    display: inline-block;
                }
                .grv-counter {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    flex-shrink: 0;
                }
                .grv-counter-input {
                    border: 1px solid #ccc;
                    border-radius: 5px;
                    background: #fff;
                    font-weight: 600;
                    color: #333;
                    width: 52px;
                    height: 39px;
                    box-sizing: border-box;
                    text-align: center;
                    font-size: 14px;
                    padding: 0;
                    font-family: inherit;
                    -moz-appearance: textfield;
                }
                .grv-counter-input::-webkit-outer-spin-button,
                .grv-counter-input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }
                .grv-counter button {
                    background: #0063A5;
                    color: #fff;
                    border: none;
                    border-radius: 6px;
                    width: 28px;
                    height: 39px;
                    box-sizing: border-box;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    font-size: 18px;
                    font-weight: bold;
                    padding: 0;
                }
                .grv-counter button:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
                .grv-counter button:first-child { border-top-right-radius: 0; border-bottom-right-radius: 0; }
                .grv-counter button:last-child { border-top-left-radius: 0; border-bottom-left-radius: 0; }
                .grv-section-title {
                    color: #247A33;
                    font-size: 24px;
                    padding: 22px 0 8px;
                }
                .grv-principles-title {
                    color: #247A33;
                    font-size: 20px;
                    padding: 0px 0 0px;
                }
                .grv-principle-text {
                    font-size: 16px;
                    line-height: 1.4;
                    color: #333;
                    margin: 0 0 10px;
                    text-align: justify;
                    white-space: pre-line;
                }
                .grv-principle-text p {
                    margin: 0 0 8px;
                    text-align: justify;
                }
                .grv-principle-text strong {
                    color: #247A33;
                }
                .grv-principle-text ul,
                .grv-principle-text ol {
                    margin: 0 0 8px;
                    padding-left: 22px;
                    text-align: justify;
                }
                .grv-principle-text li {
                    margin-bottom: 4px;
                }
                .grv-kc-panel {
                    margin-top: 4px;
                }
                .grv-kc-panel .grv-principles-title {
                    margin: 0 0 8px;
                    padding: 0;
                }
                .grv-principle-label {
                    font-size: 18px;
                    color: #247A33;
                    text-align: justify;
                }
                .grv-reflect-instructions {
                    text-align: center;
                    font-weight: 600;
                    font-size: 18px;
                    color: #263238;
                    padding: 30px 0px 18px 0px;
                }
                .grv-iv-tiles {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 14px;
                    margin: 16px 0 6px;
                }
                .grv-iv-tile {
                    flex: 0 0 200px;
                    width: 200px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    overflow: hidden;
                    background: #fff;
                    transition: box-shadow 0.2s;
                    cursor: pointer;
                    text-align: center;
                }
                .grv-iv-tile:hover {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                }
                .grv-iv-tile-open {
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    border-color: #247A33;
                }
                .grv-iv-tile-image {
                    width: 100%;
                    height: 140px;
                    overflow: hidden;
                    background: #e8f5e9;
                }
                .grv-iv-tile-image img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                    display: block;
                }
                .grv-iv-tile-label {
                    padding: 12px 14px;
                    color: #247A33;
                    font-size: 14px;
                    font-weight: 700;
                    line-height: 1.4;
                    word-break: break-word;
                }
                .grv-iv-tile-details {
                    flex: 0 0 100%;
                    width: 100%;
                    display: flex;
                    gap: 20px;
                    background: #fff;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 16px;
                    box-sizing: border-box;
                }
                .grv-iv-tile-details-image {
                    flex: 0 0 180px;
                }
                .grv-iv-tile-details-image img {
                    width: 100%;
                    border-radius: 8px;
                    border: 2px solid #2f7a3d;
                    display: block;
                }
                .grv-iv-tile-details-text {
                    flex: 1;
                    min-width: 0;
                }
                .grv-iv-tile-details-text h5 {
                    color: #247A33;
                    font-size: 16px;
                    font-weight: 700;
                    margin: 0 0 10px;
                }
                .grv-iv-tile-details-text p {
                    font-size: 15px;
                    line-height: 1.7;
                    color: #333;
                    margin: 0;
                    white-space: pre-line;
                    text-align: justify;
                }
                .grv-field textarea {
                    width: 100%;
                    box-sizing: border-box;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 10px 12px;
                    font-size: 14px;
                    min-height: 60px;
                    resize: vertical;
                    font-family: inherit;
                    background: #fff;
                }
                .grv-field-inline {
                    display: flex;
                    align-items: flex-start;
                    gap: 20px;
                    margin-bottom: 20px;
                }
                .grv-field-inline label {
                    flex: 0 0 260px;
                    max-width: 260px;
                    font-size: 15px;
                    color: #247A33;
                    font-weight: 700;
                    line-height: 1.4;
                }
                .grv-field-inline textarea,
                .grv-field-inline .grv-box {
                    flex: 1;
                    box-sizing: border-box;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 12px 14px;
                    font-size: 14px;
                    min-height: 72px;
                    resize: vertical;
                    font-family: inherit;
                    background: #fff;
                }
                @media (max-width: 700px) {
                    .grv-field-inline {
                        flex-direction: column;
                    }
                    .grv-field-inline label {
                        flex-basis: auto;
                        max-width: 100%;
                    }
                    .grv-iv-tile {
                        flex: 1 1 150px;
                        width: auto;
                        max-width: 200px;
                    }
                    .grv-iv-tile-details {
                        flex-direction: column;
                    }
                    .grv-iv-tile-details-image {
                        flex: 0 0 auto;
                        max-width: 220px;
                    }
                }
                .grv-outcome-2col {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                    align-items: stretch;
                    margin-top: 20px;
                }
                .grv-outcome-col {
                    display: flex;
                    flex-direction: column;
                }
                .grv-outcome-col > .grv-box,
                .grv-outcome-col > textarea.grv-box {
                    flex: 1 1 auto;
                    min-height: 56px;
                    width: 100%;
                    box-sizing: border-box;
                }
                .grv-outcome-eval {
                    background: #ffffff;
                    resize: vertical;
                }
                .grv-outcome-box {
                    border: 1px solid #90a4ae;
                    border-radius: 8px;
                    padding: 14px;
                    margin-top: 20px;
                    background: #fff;
                }
                .grv-outcome-box label {
                    font-weight: 700;
                    color: #247A33;
                    font-size: 13px;
                    display: block;
                    margin-bottom: 8px;
                }
                .grv-outcome-box textarea {
                    width: 100%;
                    box-sizing: border-box;
                    border: 1px solid #cfd8dc;
                    border-radius: 8px;
                    padding: 10px 12px;
                    font-size: 14px;
                    min-height: 50px;
                    resize: vertical;
                    font-family: inherit;
                }
                .grv-kc-tab {
                    font-family: "Foco", "Segoe UI", Arial, sans-serif;
                    font-weight: 700;
                    color: #323C43;
                    border-radius: 4px;
                }
                .grv-kc-tab.active {
                    background-color: #247A33;
                    color: #ffffff;
                    border-color: #247A33;
                    border-radius: 4px;
                }
                .grv-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    margin-top: 26px;
                }
                .grv-review-btn {
                    background: #fff;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 4px;
                    padding: 10px 18px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .grv-review-btn:hover {
                    border-color: #0095C7;
                    color: #247A33;
                    text-decoration: underline;
                }
                .grv-update-btn {
                    background: #247A33;
                    color: #fff;
                    border: none;
                    border-radius: 8px;
                    padding: 10px 22px;
                    font-size: 14px;
                    font-weight: 600;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    cursor: pointer;
                }
                .grv-close-btn {
                    background: #fff;
                    color: #247A33;
                    border: 1px solid #247A33;
                    border-radius: 8px;
                    padding: 10px 18px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                }
                .grv-spin {
                    animation: grv-spin 0.9s linear infinite;
                }
                .grv-modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
}
.grv-modal-card {
    background: #fff;
    border-radius: 14px;
    width: 100%;
    max-width: 360px;
    padding: 28px 24px 22px;
    text-align: center;
    position: relative;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.25);
}
.grv-modal-close-x {
    position: absolute;
    top: 10px;
    right: 10px;
    background: #323C43;
    color: #fff;
    border: none;
    border-radius: 50%;
    width: 26px;
    height: 26px;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
}
.grv-modal-icon {
    font-size: 40px;
    color: #f5c518;
    margin-bottom: 6px;
}
.grv-modal-title {
    font-size: 20px;
    font-weight: 700;
    color: #222;
    margin: 6px 0 10px;
}
.grv-modal-text {
    font-size: 14px;
    color: #555;
    line-height: 1.5;
    margin: 0 0 22px;
}
.grv-modal-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
}
.grv-modal-yes {
    background: #247A33;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    flex: 1;
}
.grv-modal-no {
    background: #d32f2f;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 10px 18px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    flex: 1;
}    
                @keyframes grv-spin {
                    to { transform: rotate(360deg); }
                }

                @media (max-width: 1024px) {
                    .grv-content {
                        padding: 16px 32px 10px;
                    }
                }

                @media (max-width: 768px) {
                    .grv-content {
                        padding: 14px 20px 10px;
                    }
                    .grv-header-bar h2 {
                        font-size: 18px;
                    }
                    .grv-client-name {
                        font-size: 19px;
                    }
                    .grv-assessment-date {
                        font-size: 18px;
                    }
                    .grv-row-2col {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }
                    .grv-row-2col.grv-status-date-row {
                        grid-template-columns: 1fr 1fr;
                    }
                    .grv-section-title {
                        font-size: 20px;
                    }
                    .grv-principles-title {
                        font-size: 17px;
                    }
                    .grv-principle-text {
                        font-size: 14px;
                    }
                    .grv-principle-label {
                        font-size: 15px;
                    }
                    .grv-field-inline {
                        flex-direction: column;
                        gap: 8px;
                    }
                    .grv-field-inline label {
                        flex-basis: auto;
                        max-width: 100%;
                    }
                    .grv-outcome-2col {
                        grid-template-columns: 1fr;
                        gap: 12px;
                    }
                    .grv-footer {
                        flex-wrap: wrap;
                    }
                }

                @media (max-width: 480px) {
                    .grv-header-bar {
                        padding: 10px 12px;
                        min-height: 44px;
                    }
                    .grv-header-bar h2 {
                        font-size: 16px;
                    }
                    .grv-back-btn,
                    .grv-back-btn2 {
                        left: 8px;
                        padding: 5px 8px;
                        font-size: 12px;
                    }
                    .grv-content {
                        padding: 12px 12px 8px;
                    }
                    .grv-client-name {
                        font-size: 17px;
                    }
                    .grv-assessment-date {
                        font-size: 15px;
                        margin-bottom: 12px;
                    }
                    .grv-goal-block {
                        padding-bottom: 18px;
                        margin-bottom: 18px;
                    }
                    .grv-field label,
                    .grv-label {
                        font-size: 14px;
                    }
                    .grv-box,
                    .grv-select {
                        font-size: 13px;
                        padding: 8px 10px;
                    }
                    .grv-subheading {
                        font-size: 13px;
                    }
                    .grv-status-note {
                        font-size: 12px;
                        padding: 10px 12px;
                    }
                    .grv-sub-title {
                        font-size: 13px;
                    }
                    .grv-pill {
                        font-size: 12px;
                        padding: 5px 10px;
                    }
                    .grv-intervention-row {
                        flex-wrap: wrap;
                    }
                    .grv-counter-field {
                        max-width: 100%;
                        flex-wrap: wrap;
                        justify-content: space-between;
                    }
                    .grv-counter-field label {
                        font-size: 12px;
                        flex: 1 1 auto;
                        min-width: 0;
                    }
                    .grv-counter {
                        flex: 0 0 auto;
                    }
                    .grv-section-title {
                        font-size: 18px;
                        margin: 18px 0 6px;
                    }
                    .grv-principles-title {
                        font-size: 15px;
                    }
                    .grv-principle-text {
                        font-size: 13px;
                    }
                    .grv-principle-label {
                        font-size: 14px;
                    }
                    .grv-reflect-instructions {
                        font-size: 13px;
                    }
                    .grv-field-inline label {
                        font-size: 14px;
                    }
                    .grv-field-inline textarea,
                    .grv-field textarea,
                    .grv-outcome-box textarea {
                        font-size: 13px;
                        padding: 10px 12px;
                    }
                    .grv-outcome-box {
                        padding: 12px;
                    }
                    .grv-footer {
                        flex-direction: column-reverse;
                        gap: 10px;
                    }
                    .grv-footer button {
                        width: 100%;
                        justify-content: center;
                    }
                }

                @media (max-width: 360px) {
                    .grv-header-bar h2 {
                        font-size: 14px;
                    }
                    .grv-back-btn,
                    .grv-back-btn2 {
                        font-size: 11px;
                        padding: 4px 7px;
                    }
                    .grv-content {
                        padding: 10px 10px 6px;
                    }
                    .grv-counter-input {
                        width: 50px;
                    }
                }

                /* ---- Goal Review summary card ---- */
                .summ-container11 {
                    width: 100%;
                    margin-top: 0px;
                    color: #333;
                    padding: 0 0 15px 0;
                }
                .sum-cont11 {
                    flex: 1;
                    padding: 0px 20px 10px 0px;
                    width: 100%;
                    
                }
                .summ-client-row {
                    text-align: center;
                    font-size: 18px;
                    font-weight: 700;
                    color: #247a33;
                    margin-bottom: 14px;
                }
                .summ-meta-row {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 16px;
                    justify-content: flex-start;
                    align-items: center;
                    margin-bottom: 18px;
                    text-align: left;
                }
                .summ-meta-label {
                    font-size: 14px;
                    font-weight: 600;
                    color: #247a33;
                }
                .summ-meta-value {
                    font-size: 14px;
                    font-weight: 500;
                    color: #58b947;
                }
                .summ-meta-pill {
                    display: inline-flex;
                    align-items: center;
                    background: #ffffff;
                    border: 1px solid #dcdcdc;
                    border-radius: 50px;
                    padding: 5px 12px;
                }
                .summ-meta-plain {
                    border: none;
                    background: transparent;
                    border-radius: 0;
                    padding: 0;
                }
                .summ-meta-green .summ-meta-label {
                    color: #58b947;
                }
                .summ-section-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #323C43;
                    margin: 6px 0 14px 0;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                }
                .summ-findings-block {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 12px;
                    margin-bottom: 10px;
                }
                .summ-finding-row {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    margin-bottom: 12px;
                    padding: 8px 12px;
                    background: #ffffff;
                    border: 1px solid #dcdcdc;
                    border-radius: 4px;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                }
                .summ-finding-label {
                    color: #323c43;
                    font-weight: 700;
                    font-size: 15px;
                    white-space: pre-wrap;
                }
                .summ-value-pill {
                    display: inline-block;
                    background: #ffffff;
                    border: 1px solid #dcdcdc;
                    border-radius: 4px;
                    padding: 4px 10px;
                    font-size: 14px;
                    color: #323c43;
                    font-weight: 500;
                }
                .summ-finding-row .summ-value-pill {
                    border: none;
                    background: transparent;
                    border-radius: 0;
                    padding: 0;
                }
                .summ-info-header {
                    font-size: 17px;
                    font-weight: 700;
                    color: #323c43;
                    margin: 20px 0 6px 0;
                    line-height: 1.4;
                }
                .summ-info-text {
                    font-size: 15px;
                    color: #323c43;
                    margin: 0 0 18px 0;
                }
                .icon-circle1 {
                    width: 32px;
                    height: 32px;
                    min-width: 32px;
                    min-height: 32px;
                    background: #247A33;
                    color: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    font-size: 12px;
                }
                @media (max-width: 768px) {
                    .sum-cont1 {
                        padding: 15px 14px 10px 14px;
                    }
                    .summ-client-row {
                        font-size: 20px;
                    }
                    .summ-meta-row {
                        gap: 10px;
                    }
                    .summ-section-title {
                        font-size: 16px;
                    }
                    .summ-finding-label {
                        font-size: 14px;
                    }
                    .summ-info-header {
                        font-size: 15px;
                    }
                    .summ-info-text {
                        font-size: 14px;
                    }
                }
            `}</style>
        </>
    );
}
